/* eslint-disable react/prop-types */
import {
	CoreButton,
	CoreDividerWithText,
	CoreIconButton,
	CoreMenuButton,
	CoreMenuItem,
	CoreTable,
	CoreTableColumn,
	CoreTableRow,
	CoreTextField,
	coreToast,
	CoreUpload,
} from "@verity/components/core";
import i18n from "../../../../i18n";
import { useRef, useState } from "react";
import { Box, Link, ListItemText, Stack, Typography } from "@mui/material";
import styles from "./ScanSection.module.css";
import { Trans } from "react-i18next";
import { parse } from "papaparse";
import CheckMarkIcon from "../CreateStandaloneAssessmentSection/check-mark.svg?react";
import CancelIcon from "../CreateStandaloneAssessmentSection/cancel-icon.svg?react";
import { useMobileLayout } from "@verity/components";
import { triggerFileDownload } from "../../../../helpers/download";
import { QuestionNumber } from "./TakedownSection";

type RowData = { question: string };
type EditedRow = { data: RowData; index: number; absoluteIndex: number };

export default function UploadSection({
	onUpload,
	numMissingQuestions,
}: {
	onUpload: (questions: string[]) => void;
	numMissingQuestions: number;
}) {
	const [questions, setQuestions] = useState<RowData[]>([]);
	const [currentlyEditedRow, setCurrentlyEditedRow] = useState<EditedRow | null>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const isMobileLayout = useMobileLayout();

	function handleFileUpload(file: File) {
		if (!file) return;

		if (!file.name.toLowerCase().endsWith(".csv")) {
			coreToast.dismiss();
			coreToast.error(i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.errors.fileType"), {
				autoClose: 2500,
			});
			return;
		}

		parse(file, {
			header: true,
			skipEmptyLines: true,
			complete({ data, meta }) {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const fields = meta.fields!;
				const headerName = i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.csv.header");
				const missingHeaders = !fields.includes(headerName);
				if (missingHeaders) {
					coreToast.error(
						i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.errors.missingHeaders", {
							headerName: headerName,
						}),
					);
					return;
				}

				const parsedRows: RowData[] = (data as Record<string, string>[]).map((d) => ({
					question: d[headerName].trim(),
				}));

				if (parsedRows.length === 0) {
					coreToast.error(i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.errors.noQuestions"));
					return;
				}

				const cappedRows = parsedRows.slice(0, numMissingQuestions);

				setQuestions(cappedRows);
				coreToast.dismiss();
				if (parsedRows.length > numMissingQuestions) {
					coreToast.warning(
						i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.errors.tooManyQuestions", {
							count: numMissingQuestions,
							actual: parsedRows.length,
						}),
						{ autoClose: 3500, size: "large" },
					);
				} else {
					coreToast.success(i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.success"), {
						autoClose: 2500,
						size: "large",
					});
				}
			},
		});
	}

	const handleDownloadCSV = () => {
		const questions = [
			i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.csv.row1"),
			i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.csv.row2"),
		];

		const header = i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.csv.header"); // "Questions"
		const csvContent = [header, ...questions].map((line) => `"${line.replace(/"/g, '""')}"`).join("\n");

		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const fileName = `${i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.csv.fileName")}.csv`;

		triggerFileDownload(blob, fileName);
	};

	const isRowCurrentlyEdited = (row: CoreTableRow<RowData>) => {
		return row.absoluteIndex === currentlyEditedRow?.absoluteIndex;
	};

	const dropdownOptions = [
		{
			name: "Edit",
			onClick: (row: CoreTableRow<RowData>) => {
				setCurrentlyEditedRow({
					data: row.data,
					index: row.index,
					absoluteIndex: row.absoluteIndex,
				});
			},
		},
		{
			name: "Delete",
			onClick: (row: CoreTableRow<RowData>) => {
				// remove the question by absoluteIndex
				setQuestions((prev) => prev.filter((_, i) => i !== row.absoluteIndex));
				// if the deleted row is the one being edited, cancel edit
				setCurrentlyEditedRow((cur) => (cur?.absoluteIndex === row.absoluteIndex ? null : cur));
			},
		},
	];

	const ActionColumn = ({ row }: { row: CoreTableRow<RowData> }) => {
		const [menuShowing, setMenuShowing] = useState(false);

		const disableSaveIcon =
			currentlyEditedRow?.data.question === undefined || currentlyEditedRow.data.question.trim() === "";

		return isRowCurrentlyEdited(row) ? (
			<Stack direction={"row"} justifyContent={"center"}>
				<CoreIconButton
					tooltip={i18n.t(
						"portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.ariaLabels.cancelIcon",
					)}
					onClick={() => {
						setCurrentlyEditedRow(null);
					}}
					variant="plain"
				>
					<CancelIcon />
				</CoreIconButton>{" "}
				<CoreIconButton
					tooltip={
						disableSaveIcon
							? i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.disabledSaveTooltip")
							: i18n.t(
									"portal.components.assessmentPage.createStandAloneQuizDialog.inviteeInfoTableTSX.ariaLabels.saveIcon",
								)
					}
					variant="plain"
					disabled={disableSaveIcon}
					onClick={() => {
						if (currentlyEditedRow) {
							setQuestions((prev) => {
								const newQuestions = [...prev];
								newQuestions[currentlyEditedRow.absoluteIndex].question = currentlyEditedRow.data.question;
								return newQuestions;
							});
						}
						setCurrentlyEditedRow(null);
					}}
				>
					<CheckMarkIcon />
				</CoreIconButton>
			</Stack>
		) : (
			<Box className={styles.actionButtonWrapper}>
				<CoreMenuButton menuShowing={menuShowing} setMenuShowing={setMenuShowing}>
					{dropdownOptions.map((item) => {
						return (
							<CoreMenuItem
								key={item.name}
								onClick={() => {
									item.onClick(row);
									setMenuShowing(false);
								}}
							>
								<ListItemText primary={item.name} />
							</CoreMenuItem>
						);
					})}
				</CoreMenuButton>
			</Box>
		);
	};

	const columns: CoreTableColumn<RowData>[] = [
		{
			key: "questionNumber",
			header: i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.column.questionNumber"),
			cell: (row) => (
				<Box justifyContent={"center"} display={"flex"}>
					<QuestionNumber questionNumber={row.index + 1} />
				</Box>
			),
			tableCellContentWidth: "100%",
			width: "12%",
		},
		{
			key: "question",
			header: i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.column.question"),
			width: "72%",
			tableCellContentWidth: "100%",
			allowBodyWrap: true,
			cell: (row) => {
				return isRowCurrentlyEdited(row) && currentlyEditedRow ? (
					<CoreTextField
						label={i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.questionPlaceholder")}
						value={currentlyEditedRow.data.question}
						onChange={(e) => {
							e.preventDefault();
							setCurrentlyEditedRow((prev) => (prev ? { ...prev, data: { question: e.target.value } } : prev));
						}}
						size="small"
						fullWidth
						autoFocus={true}
						inputProps={{ required: true }}
					/>
				) : (
					row.data.question
				);
			},
		},
		{
			key: "action",
			header: i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.column.action"),
			cell: (row) => <ActionColumn row={row} />,
			width: "16%",
			alignHeader: "center",
			tableCellContentWidth: "100%",
			align: "center",
		},
	];

	return (
		<Stack spacing={1}>
			<CoreDividerWithText className={styles.dividerText} />
			<Stack spacing={0.5} padding={"0 5px"}>
				<Typography className={styles.uploadTitle}>
					{i18n.t(`portal.components.contentProtectionPageTSX.scanAndCheck.upload.title`)}
				</Typography>
				<Stack
					direction={isMobileLayout ? "column" : "row"}
					justifyContent={isMobileLayout ? "flex-start" : "space-between"}
					alignItems={isMobileLayout ? "center" : "flex-start"}
					spacing={10}
				>
					<Typography component="span" className={styles.uploadDescription}>
						<Trans
							i18nKey="portal.components.contentProtectionPageTSX.scanAndCheck.upload.description"
							components={{
								a: <Link onClick={handleDownloadCSV} component="button" />,
							}}
						/>
					</Typography>
					<Stack direction="row" spacing={1} ref={containerRef} tabIndex={-1} className={styles.buttonContainer}>
						<CoreButton
							color="cancel"
							onClick={() => {
								setQuestions([]);
								coreToast.dismiss();
								coreToast.success(
									i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.removeCSVSuccess"),
									{ autoClose: 2500 },
								);
								containerRef.current?.focus();
							}}
							disabled={questions.length === 0}
							className={styles.csvUploadButtons}
						>
							{i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.removeCSV")}
						</CoreButton>
						<CoreButton
							className={styles.csvUploadButtons}
							onClick={() => onUpload(questions.map((row) => row.question))}
							disabled={questions.length === 0}
						>
							{i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.upload")}
						</CoreButton>
					</Stack>
				</Stack>
			</Stack>
			{questions.length > 0 ? (
				<CoreTable
					data={questions}
					columns={columns}
					rowsPerPage={10}
					unhoverableRows={currentlyEditedRow ? [currentlyEditedRow.absoluteIndex] : undefined}
				/>
			) : (
				<CoreUpload
					fileTypes=".csv"
					description={i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.dragAndDrop")}
					buttonText={i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.upload.chooseFileButton")}
					onUpload={(files: File[]) => handleFileUpload(files[0])}
					className={styles.uploadArea}
				/>
			)}
		</Stack>
	);
}
